import React, { memo, useState } from 'react';
import * as Sentry from '@sentry/react';
import { Button } from '@heroui/react';
import { Buildings, StackSimple, UsersThree, XCircle } from '@phosphor-icons/react';
import { toast } from 'react-toastify';
import { useMassMailContext } from '../context/MassMailContext';
import { SelectedResource } from '../types';
import { useMassMailData } from '../hooks/useMassMailData';
import DirectSearchComponent from 'shared/components/searchBar/components/directSearchComponent';
import { ResourceType } from 'shared/types/resource';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const ALL_TENANTS_RESOURCE_ID = 'all-tenants';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * Haalt het e-mailadres uit een zoekresultaat. `contactDetails` komt uit de detail-fetch
 * van DirectSearchComponent; faalt die, dan bevat `metadata1` bij contacten/firma's soms
 * het adres (maar even goed een firmanaam of website) — daarom de patroontoets.
 */
const extractEmail = (resource: {
  contactDetails?: { email?: string };
  metadata1?: unknown;
}): string => {
  const fromDetails = resource.contactDetails?.email?.trim();
  if (fromDetails && EMAIL_PATTERN.test(fromDetails)) return fromDetails;
  const fromMetadata = typeof resource.metadata1 === 'string' ? resource.metadata1.trim() : '';
  return EMAIL_PATTERN.test(fromMetadata) ? fromMetadata : '';
};

export const ResourceSelector: React.FC = memo(() => {
  const { state, dispatch } = useMassMailContext();
  const { loadContactsForResource, fetchAllActiveContacts } = useMassMailData();
  const [confirmAllOpen, setConfirmAllOpen] = useState(false);
  const [isLoadingAll, setIsLoadingAll] = useState(false);

  const isAllTenantsSelected = state.selectedResources.some(r => r.type === 'all-tenants');

  const handleSelectAllTenants = async () => {
    setIsLoadingAll(true);
    dispatch({ type: 'SET_LOADING_CONTACTS', payload: true });

    try {
      const allContacts = await fetchAllActiveContacts();

      if (allContacts.length === 0) {
        toast.info('Geen actieve huurders gevonden');
        return;
      }

      const newSelectedResource: SelectedResource = {
        id: ALL_TENANTS_RESOURCE_ID,
        resourceId: ALL_TENANTS_RESOURCE_ID,
        type: 'all-tenants',
        name: 'Alle huurders',
        contactCount: allContacts.length,
      };

      dispatch({
        type: 'SET_SELECTED_RESOURCES',
        payload: [...state.selectedResources, newSelectedResource],
      });

      const existingIds = new Set(state.contacts.map(c => c.id));
      const newContacts = allContacts.filter(c =>
        !existingIds.has(c.id) && !state.excludedContactIds.has(c.id),
      );

      dispatch({
        type: 'SET_CONTACTS',
        payload: [...state.contacts, ...newContacts],
      });

      toast.success(`${newContacts.length} huurders toegevoegd`);
    } catch (error) {
      console.error('❌ Error selecting all tenants:', error);
      Sentry.captureException(error, {
        tags: { component: 'ResourceSelector', action: 'handleSelectAllTenants' },
      });
      toast.error('Fout bij het toevoegen van alle huurders');
    } finally {
      setIsLoadingAll(false);
      setConfirmAllOpen(false);
      dispatch({ type: 'SET_LOADING_CONTACTS', payload: false });
    }
  };

  const handleAddParticipant = async (resource: any) => {
    console.log('🔍 DirectSearchComponent selected resource:', resource); // Debug log
    
    dispatch({ type: 'SET_LOADING_CONTACTS', payload: true });

    try {
      console.log('🔍 Resource type:', resource.type, 'Expected BUILDING:', ResourceType.BUILDING);
      
      if (resource.type === ResourceType.BUILDING) {
        // Check if building is already selected
        const isAlreadySelected = state.selectedResources.some(r => 
          r.resourceId === resource.resourceId && r.type === 'building'
        );
        if (isAlreadySelected) {
          toast.info("Dit gebouw is al geselecteerd");
          return;
        }

        console.log('📥 Loading contacts for building:', resource.resourceId);
        
        // Fetch contacts from building
        const buildingContacts = await loadContactsForResource({
          id: resource.id,
          resourceId: resource.resourceId,
          type: 'building',
          name: resource.name,
          contactCount: 0
        });

        console.log('📥 Loaded building contacts:', buildingContacts.length);

        // Add building to selected resources
        const newSelectedResource: SelectedResource = {
          id: resource.id,
          resourceId: resource.resourceId,
          type: 'building',
          name: resource.name,
          contactCount: buildingContacts.length
        };
        
        dispatch({
          type: 'SET_SELECTED_RESOURCES',
          payload: [...state.selectedResources, newSelectedResource]
        });

        // Add contacts to existing contacts (avoiding duplicates and excluded contacts)
        const existingIds = new Set(state.contacts.map(c => c.id));
        const newContacts = buildingContacts.filter(c => 
          !existingIds.has(c.id) && !state.excludedContactIds.has(c.id)
        );
        
        console.log('📥 Adding new contacts (after deduplication & exclusions):', newContacts.length);
        
        dispatch({
          type: 'SET_CONTACTS',
          payload: [...state.contacts, ...newContacts]
        });

        console.log(`✅ Added building with ${buildingContacts.length} contacts`);

      } else if (resource.type === ResourceType.UNIT) {
        // Check if unit is already selected
        const isAlreadySelected = state.selectedResources.some(r => 
          r.resourceId === resource.resourceId && r.type === 'unit'
        );
        if (isAlreadySelected) {
          toast.info("Deze unit is al geselecteerd");
          return;
        }

        console.log('📥 Loading contacts for unit:', resource.resourceId);
        
        // Fetch contacts from unit
        const unitContacts = await loadContactsForResource({
          id: resource.id,
          resourceId: resource.resourceId,
          type: 'unit',
          name: resource.name,
          contactCount: 0
        });

        console.log('📥 Loaded unit contacts:', unitContacts.length);

        // Add unit to selected resources
        const newSelectedResource: SelectedResource = {
          id: resource.id,
          resourceId: resource.resourceId,
          type: 'unit',
          name: resource.name,
          contactCount: unitContacts.length
        };
        
        dispatch({
          type: 'SET_SELECTED_RESOURCES',
          payload: [...state.selectedResources, newSelectedResource]
        });

        // Add contacts to existing contacts (avoiding duplicates and excluded contacts)
        const existingIds = new Set(state.contacts.map(c => c.id));
        const newContacts = unitContacts.filter(c => 
          !existingIds.has(c.id) && !state.excludedContactIds.has(c.id)
        );
        
        console.log('📥 Adding new contacts (after deduplication & exclusions):', newContacts.length);
        
        dispatch({
          type: 'SET_CONTACTS',
          payload: [...state.contacts, ...newContacts]
        });

        console.log(`✅ Added unit with ${unitContacts.length} contacts`);

      } else if (resource.type === ResourceType.COMPANY || resource.type === ResourceType.COMPANIES) {
        // Firma's als ontvanger: het e-mailadres van de firma zelf. Zonder deze tak
        // werd een geselecteerde firma stilzwijgend genegeerd.
        const companyEmail = extractEmail(resource);
        const companyId = String(resource.id);

        if (state.contacts.some(c => c.id === companyId)) {
          toast.info('Deze firma is al geselecteerd');
          return;
        }

        if (!companyEmail) {
          toast.warning(`${resource.name} heeft geen e-mailadres — vul dit aan bij de firmagegevens`);
          Sentry.captureMessage('MassMail: firma zonder e-mailadres geselecteerd', {
            level: 'warning',
            extra: { companyId, name: resource.name },
          });
          return;
        }

        dispatch({
          type: 'SET_CONTACTS',
          payload: [
            ...state.contacts,
            {
              id: companyId,
              name: resource.name,
              resourceId: resource.resourceId,
              firstName: resource.name,
              lastName: '',
              phoneMobile: resource.contactDetails?.phone || '',
              email: companyEmail,
              units: [],
              buildingId: '',
              buildingName: '',
              unitId: '',
              unitNumber: '',
            },
          ],
        });

        toast.success(`${resource.name} toegevoegd als ontvanger`);
      } else if (resource.type === ResourceType.CONTACT) {
        // Check if contact is already selected or excluded
        const isAlreadySelected = state.contacts.some(c => c.id === resource.id);
        const isExcluded = state.excludedContactIds.has(resource.id);
        
        if (isAlreadySelected) {
          toast.info("Dit contact is al geselecteerd");
          return;
        }
        
        if (isExcluded) {
          toast.info("Dit contact werd eerder verwijderd uit de lijst");
          return;
        }

        // `contactDetails` komt uit een extra fetch in DirectSearchComponent. Faalt die,
        // dan val terug op wat de zoekresultaten zelf meegeven — anders belandt het
        // contact zonder adres in de lijst en blokkeert de verzendknop zonder reden.
        const contactEmail = extractEmail(resource);

        if (!contactEmail) {
          toast.warning(
            `${resource.name} heeft geen e-mailadres — vul dit aan bij de contactgegevens`
          );
          Sentry.captureMessage('MassMail: contact zonder e-mailadres geselecteerd', {
            level: 'warning',
            extra: { contactId: resource.id, name: resource.name },
          });
        }

        // Add contact directly (FIXED to match legacy modal)
        const newContact = {
          id: resource.id,
          name: resource.name,
          resourceId: resource.resourceId,
          firstName: resource.contactDetails?.firstName || '',
          lastName: resource.contactDetails?.lastName || '',
          phoneMobile: resource.metadata1 || resource.contactDetails?.phoneMobile || "GSM-nummer onbekend",
          email: contactEmail,
          units: [],
          buildingId: resource.buildingId || '',
          buildingName: resource.buildingName || '',
          unitId: resource.unitId || '',
          unitNumber: resource.unitNumber || ''
        };

        dispatch({
          type: 'SET_CONTACTS',
          payload: [...state.contacts, newContact]
        });

        console.log("✅ Added individual contact:", newContact.name);
      } else {
        // Onbekend resourcetype: nooit stil laten mislukken.
        toast.warning('Dit type kan niet als ontvanger toegevoegd worden');
        Sentry.captureMessage('MassMail: onbekend resourcetype als ontvanger', {
          level: 'warning',
          extra: { type: resource.type, name: resource.name },
        });
      }

    } catch (error) {
      console.error("❌ Error adding participant:", error);
      toast.error("Fout bij het toevoegen van de resource");
    } finally {
      dispatch({ type: 'SET_LOADING_CONTACTS', payload: false });
    }
  };

  const removeResource = async (resourceToRemove: SelectedResource) => {
    dispatch({ type: 'SET_LOADING_CONTACTS', payload: true });
    
    try {
      // Remove the resource from selectedResources
      const updatedResources = state.selectedResources.filter(r => 
        !(r.resourceId === resourceToRemove.resourceId && r.type === resourceToRemove.type)
      );
      dispatch({
        type: 'SET_SELECTED_RESOURCES',
        payload: updatedResources
      });

      // Load contacts that should be removed
      const contactsFromResource = await loadContactsForResource(resourceToRemove);
      
      if (contactsFromResource.length > 0) {
        const idsToRemove = new Set(contactsFromResource.map(c => c.id));
        const updatedContacts = state.contacts.filter(c => !idsToRemove.has(c.id));
        
        dispatch({
          type: 'SET_CONTACTS',
          payload: updatedContacts
        });
        
        console.log(`Removed ${contactsFromResource.length} contacts with resource:`, resourceToRemove);
      }
    } catch (error) {
      console.error('Error removing resource:', error);
    } finally {
      dispatch({ type: 'SET_LOADING_CONTACTS', payload: false });
    }
  };

  return (
      <div className="space-y-4">
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="space-y-3">
            <p className="text-sm font-medium text-gray-700">
              <Buildings className="inline h-4 w-4 mr-1" />
              Gebouwen, units, contactpersonen en firma's selecteren <span className="text-danger">*</span>
            </p>
            <DirectSearchComponent
              resourceFilter={[
                ResourceType.BUILDING,
                ResourceType.UNIT,
                ResourceType.CONTACT,
                ResourceType.COMPANY,
              ]}
              onSelect={handleAddParticipant}
              label="Geadresseerde toevoegen"
              excludeRenovationProjects={true}
            />
            <div className="flex items-center gap-2 pt-1">
              <span className="text-xs text-gray-500">of</span>
              <Button
                size="sm"
                variant="bordered"
                startContent={<UsersThree size={16} />}
                onPress={() => setConfirmAllOpen(true)}
                isDisabled={isAllTenantsSelected || isLoadingAll}
                isLoading={isLoadingAll}
              >
                {isAllTenantsSelected
                  ? 'Alle huurders geselecteerd'
                  : 'Selecteer alle huurders'}
              </Button>
            </div>
          </div>
        </div>

        <AlertDialog open={confirmAllOpen} onOpenChange={setConfirmAllOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Alle huurders selecteren?</AlertDialogTitle>
              <AlertDialogDescription>
                Hiermee worden alle huurders met een actief huurcontract toegevoegd
                als geadresseerden. Dit kan een groot aantal contacten zijn.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isLoadingAll}>Annuleren</AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => {
                  e.preventDefault();
                  handleSelectAllTenants();
                }}
                disabled={isLoadingAll}
                className="bg-brick-primary hover:bg-brick-primary/90 text-white"
              >
                {isLoadingAll ? 'Bezig met laden...' : 'Bevestigen'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Selected Resources */}
        {state.selectedResources.length > 0 && (
          <div className="space-y-2">
            <div className="text-sm font-medium text-gray-700">
              Geselecteerde bronnen:
            </div>
            <div className="flex flex-wrap gap-2">
              {state.selectedResources.map((resource) => (
                <div
                  key={`${resource.type}-${resource.resourceId}`}
                  className="flex w-fit items-center gap-2 rounded-full border border-gray-300 p-2 text-sm text-gray-700"
                >
                  <div className="flex items-center gap-1">
                    {resource.type === 'building' ? (
                      <Buildings size={14} />
                    ) : resource.type === 'all-tenants' ? (
                      <UsersThree size={14} />
                    ) : (
                      <StackSimple size={14} />
                    )}
                    <span className="font-medium">{resource.name}</span>
                    <span className="text-xs text-gray-500">
                      ({resource.contactCount})
                    </span>
                  </div>
                  <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    color="danger"
                    onPress={() => removeResource(resource)}
                  >
                    <XCircle size={14} />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
    </div>
  );
});
